import {
	HttpClient,
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpHeaders,
	HttpInterceptor,
	HttpRequest,
	HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { catchError, finalize, shareReplay, switchMap, tap } from 'rxjs/operators';
import { HttpStatusEnum, NetworkClientBuilder, PlatformsEnum, setTokens } from 'auth';
import { environment } from '../../../environments/environment';
import { throwError } from 'rxjs';
import { responseVersionHandler } from '../utils/version-popup.utils';
import { ACCESS_TOKEN_HEADER, ID_TOKEN_HEADER, REFRESH_TOKEN_HEADER, USERNAME_HEADER, Tokens } from 'auth';
import { AuthWorker } from '@app/shared/services/auth/AuthWorker';

const AUTH_HOST: string = environment.CS_URLS.AUTH_ROOT;
const REFRESH_URL: string = `${AUTH_HOST}/refresh`;
const FAILED_AUTH_RESULT: string = 'JWT_AUTHENTICATION_FAILED';
const AUTH_HEADER: string = 'X-Auth-Status';

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {
	private refreshTokenObservable: Observable<Tokens> | null = null;

	constructor(private http: HttpClient) {}

	/**
	 * Intercepts requests and refreshes the token if it is expired
	 * If the response is 401 and the 'X-Auth-Status' header is 'JWT_AUTHENTICATION_FAILED',
	 * then it will call the refresh endpoint.
	 * Checks if the request is for the refresh endpoint and if so, it will not call the refresh endpoint again.
	 * @param request {HttpRequest<any>} - The request object
	 * @param next {HttpHandler} - The next handler
	 * @return {Observable<HttpEvent<any>>} - The observable of the response
	 */
	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const tokens: Partial<Tokens> = AuthWorker.getTokens();

		if (!request.url.includes(environment.CS_URLS.API_DOMAIN) || request.url.includes(environment.CS_URLS.AUTH_ROOT)) {
			return next.handle(request);
		}

		const clonedRequest: HttpRequest<any> = request.clone({
			withCredentials: NetworkClientBuilder.includeCredentials,
			headers: new HttpHeaders({
				...(Object.fromEntries(request.headers.keys().map(key => [key, request.headers.getAll(key)])) || {}),
				...(tokens.accessToken && { [ACCESS_TOKEN_HEADER]: tokens.accessToken }),
				...(tokens.userIdToken && { [ID_TOKEN_HEADER]: tokens.userIdToken }),
				...(tokens.username && { [USERNAME_HEADER]: tokens.username }),
			}),
		});

		// if the request is for the refresh endpoint, do not call the refresh endpoint again.
		if (clonedRequest.url.includes(REFRESH_URL)) {
			return next.handle(clonedRequest);
		}

		return next.handle(clonedRequest).pipe(
			tap((event: HttpEvent<any>) => {
				if (event instanceof HttpResponse && !event.url?.includes(AUTH_HOST)) {
					responseVersionHandler(event);
				}
			}),
			catchError((error: HttpErrorResponse) => {
				// if the error is 401 and the "X-AUTH_STATUS" header is "JWT_AUTHENTICATION_FAILED", call the refresh endpoint.
				if (error.status === HttpStatusEnum.UNAUTHORIZED && error.headers.get(AUTH_HEADER) === FAILED_AUTH_RESULT) {
					// If refresh is already in progress, reuse the existing observable
					if (!this.refreshTokenObservable) {
						const { refreshToken, username } = AuthWorker.getTokens();

						this.refreshTokenObservable = this.http
							.get<Tokens>(`${REFRESH_URL}?platform=${PlatformsEnum.BO}`, {
								withCredentials: NetworkClientBuilder.includeCredentials,
								headers: new HttpHeaders({
									...(refreshToken && { [REFRESH_TOKEN_HEADER]: refreshToken }),
									...(username && { [USERNAME_HEADER]: username }),
								}),
							})
							.pipe(
								shareReplay(1), // Share the observable so all subscribers get the same result
								finalize(() => {
									// Clear the observable after completion (success or error)
									this.refreshTokenObservable = null;
								})
							);
					}

					return this.refreshTokenObservable.pipe(
						catchError((error: HttpErrorResponse) => {
							return throwError(error);
						}),
						switchMap((newTokens: Tokens) => {
							setTokens(newTokens);

							const headers = new HttpHeaders({
								...(Object.fromEntries(
									clonedRequest.headers.keys().map(key => [key, clonedRequest.headers.getAll(key)])
								) || {}),
								...(newTokens.accessToken && { [ACCESS_TOKEN_HEADER]: newTokens.accessToken }),
								...(newTokens.userIdToken && { [ID_TOKEN_HEADER]: newTokens.userIdToken }),
								...(newTokens.username && { [USERNAME_HEADER]: newTokens.username }),
							});

							return next.handle(
								clonedRequest.clone({ headers, withCredentials: NetworkClientBuilder.includeCredentials })
							);
						})
					);
				}

				return throwError(error);
			})
		);
	}
}
